import { useCallback, useEffect, useState } from 'react';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { ptBR } from '@/i18n/pt-BR';
import { roleLabel } from '@/lib/roles';
import { useApiClient } from '@/lib/api';
import {
  EscritoriosError,
  deleteMyMember,
  listMyMembers,
  updateMyMember,
  type EscritoriosErrorReason,
  type MemberRecord,
  type MemberRoleUpdatePayload,
} from '@/lib/escritorios';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import { CreateMemberDialog } from '@/components/Members/CreateMemberDialog';
import { mapMembersErrorReason } from '@/components/Members/error_messages';
import { Blueprint, Button } from '@/components/ui';

function _formatDate(iso: string): string {
  // ux-neutrality: data ISO → pt-BR. Sem dependência de lib externa.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
}

interface MemberRowProps {
  member: MemberRecord;
  canRemove: boolean;
  canManageRole: boolean;
  canManageCertificacao: boolean;
  removeBusyId: string | null;
  roleBusyId: string | null;
  onRemove(member: MemberRecord): void;
  onSetRole(member: MemberRecord, role: MemberRoleUpdatePayload['role']): void;
}

function MemberRow({
  member,
  canRemove,
  canManageRole,
  canManageCertificacao,
  removeBusyId,
  roleBusyId,
  onRemove,
  onSetRole,
}: MemberRowProps) {
  const isRevisor = member.role === 'revisor';
  const isCertificador = member.role === 'certificador';
  // E52.F1.C02: o toggle genérico só vale entre `member` e `revisor`. Sobre um
  // certificador ele enviaria `revisor` e apagaria o poder de assinatura numa
  // operação rotineira de gestão de usuários (F-E52.F1-02).
  const showRoleToggle = canManageRole && !isCertificador;
  return (
    <Blueprint as="li" data-testid={`member-row-${member.id}`} className="p-sc-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-body-md text-fg" data-testid="member-row-email">
            {member.email}
          </p>
          <p className="mt-1 text-body-sm text-fg-muted">
            {ptBR.members.itemRoleLabel}:{' '}
            <span data-testid="member-row-role">{roleLabel(member.role)}</span>
            {' · '}
            {ptBR.members.itemStatusLabel}:{' '}
            {member.is_active ? ptBR.members.statusActive : ptBR.members.statusInactive}
            {' · '}
            {ptBR.members.itemCreatedAtLabel} {_formatDate(member.created_at)}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {showRoleToggle ? (
            <Button
              variant="secondary"
              type="button"
              onClick={() => onSetRole(member, isRevisor ? 'member' : 'revisor')}
              disabled={roleBusyId !== null}
              data-testid={`member-row-role-toggle-${member.id}`}
            >
              {isRevisor ? ptBR.members.demoteToMemberCta : ptBR.members.promoteToRevisorCta}
            </Button>
          ) : null}
          {canManageCertificacao ? (
            <Button
              variant="secondary"
              type="button"
              onClick={() => onSetRole(member, isCertificador ? 'member' : 'certificador')}
              disabled={roleBusyId !== null}
              data-testid={`member-row-certificador-toggle-${member.id}`}
            >
              {isCertificador
                ? ptBR.members.revokeCertificadorCta
                : ptBR.members.grantCertificadorCta}
            </Button>
          ) : null}
          {canRemove ? (
            <Button
              variant="secondary"
              type="button"
              onClick={() => onRemove(member)}
              disabled={removeBusyId !== null}
              data-testid={`member-row-remove-${member.id}`}
            >
              {ptBR.members.removeCta}
            </Button>
          ) : null}
        </div>
      </div>
    </Blueprint>
  );
}

interface MembersListProps {
  loading: boolean;
  loadError: string | null;
  items: MemberRecord[] | null;
  isOwner: boolean;
  isPlatform: boolean;
  removeBusyId: string | null;
  roleBusyId: string | null;
  onRetry(): void;
  onRemove(member: MemberRecord): void;
  onSetRole(member: MemberRecord, role: MemberRoleUpdatePayload['role']): void;
}

function MembersList({
  loading,
  loadError,
  items,
  isOwner,
  isPlatform,
  removeBusyId,
  roleBusyId,
  onRetry,
  onRemove,
  onSetRole,
}: MembersListProps) {
  if (loading) {
    return (
      <p className="mt-8 text-body-md text-fg-muted" data-testid="members-loading">
        {ptBR.members.listLoading}
      </p>
    );
  }
  if (loadError !== null) {
    return (
      <div
        className="mt-8 border border-danger bg-danger-50 px-4 py-3"
        data-testid="members-load-error"
      >
        <p className="text-body-sm text-danger-700">{loadError}</p>
        <Button variant="secondary" type="button" onClick={onRetry} className="mt-3">
          {ptBR.members.listErrorRetry}
        </Button>
      </div>
    );
  }
  if (items !== null && items.length === 0) {
    return (
      <div className="mt-8" data-testid="members-empty">
        <h2 className="text-heading-md text-fg">{ptBR.members.listEmptyTitle}</h2>
        <p className="mt-2 text-body-sm text-fg-muted">{ptBR.members.listEmptyHint}</p>
      </div>
    );
  }
  return (
    <ul className="mt-8 flex flex-col gap-3" data-testid="members-list">
      {items?.map((m) => (
        <MemberRow
          key={m.id}
          member={m}
          canRemove={isOwner && m.role !== 'owner'}
          canManageRole={isOwner && m.role !== 'owner'}
          canManageCertificacao={isOwner && isPlatform && m.role !== 'owner'}
          removeBusyId={removeBusyId}
          roleBusyId={roleBusyId}
          onRemove={onRemove}
          onSetRole={onSetRole}
        />
      ))}
    </ul>
  );
}

export function MembersPage() {
  const apiClient = useApiClient();
  const { role, isPlatform, loading: roleLoading } = useCurrentRole();
  const [items, setItems] = useState<MemberRecord[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<MemberRecord | null>(null);
  const [removeBusyId, setRemoveBusyId] = useState<string | null>(null);
  const [roleBusyId, setRoleBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listMyMembers(apiClient);
      setItems(list);
    } catch (err) {
      const reason: EscritoriosErrorReason =
        err instanceof EscritoriosError ? err.reason : 'unknown';
      setLoadError(mapMembersErrorReason(reason));
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function handleAddSuccess(item: MemberRecord) {
    setItems((prev) => (prev ? [...prev, item] : [item]));
    setAddOpen(false);
    setActionError(null);
    setActionSuccess(ptBR.members.saveSuccess);
  }

  async function handleSetRole(member: MemberRecord, nextRole: MemberRoleUpdatePayload['role']) {
    setRoleBusyId(member.id);
    setActionError(null);
    try {
      const updated = await updateMyMember(apiClient, member.id, { role: nextRole });
      setItems((prev) => (prev ? prev.map((it) => (it.id === member.id ? updated : it)) : prev));
      setActionSuccess(ptBR.members.roleUpdateSuccess);
    } catch (err) {
      const reason: EscritoriosErrorReason =
        err instanceof EscritoriosError ? err.reason : 'unknown';
      setActionError(mapMembersErrorReason(reason));
    } finally {
      setRoleBusyId(null);
    }
  }

  async function handleRemoveConfirm() {
    if (removeTarget === null) return;
    const target = removeTarget;
    setRemoveBusyId(target.id);
    setActionError(null);
    try {
      await deleteMyMember(apiClient, target.id);
      setItems((prev) => (prev ? prev.filter((it) => it.id !== target.id) : prev));
      setActionSuccess(ptBR.members.removeSuccess);
      setRemoveTarget(null);
    } catch (err) {
      const reason: EscritoriosErrorReason =
        err instanceof EscritoriosError ? err.reason : 'unknown';
      setActionError(mapMembersErrorReason(reason));
    } finally {
      setRemoveBusyId(null);
    }
  }

  const isOwner = role === 'owner';
  const showAddCta = isOwner;
  // E52.F1.C02: conceder/revogar certificação só faz sentido no
  // escritório-plataforma — é lá que o papel tem efeito, e é o mesmo recorte
  // que o servidor exige (`is_platform` do GUC, não do payload).
  const canManageCertificacao = isOwner && isPlatform;

  return (
    <section
      aria-labelledby="members-title"
      data-testid="members-page"
      className="mx-auto max-w-3xl"
    >
      <p className="mb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
        {ptBR.members.eyebrow}
      </p>
      <h1 id="members-title" className="text-display-md text-fg">
        {ptBR.members.pageTitle}
      </h1>
      <p className="mt-2 text-body-md text-fg-muted">{ptBR.members.intro}</p>

      {showAddCta ? (
        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="members-add-cta"
            disabled={roleLoading}
          >
            {ptBR.members.addCta}
          </Button>
        </div>
      ) : null}

      {actionSuccess !== null ? (
        <p
          role="status"
          data-testid="members-action-success"
          className="mt-4 border border-success bg-success-50 px-3 py-2 text-body-sm text-success-700"
        >
          {actionSuccess}
        </p>
      ) : null}

      {actionError !== null ? (
        <p
          role="alert"
          data-testid="members-action-error"
          className="mt-4 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
        >
          {actionError}
        </p>
      ) : null}

      {isOwner ? (
        <p className="mt-4 text-body-sm text-fg-muted" data-testid="members-role-hint">
          {ptBR.members.roleHint}
        </p>
      ) : null}

      {canManageCertificacao ? (
        <p className="mt-2 text-body-sm text-fg-muted" data-testid="members-certificador-hint">
          {ptBR.members.certificadorHint}
        </p>
      ) : null}

      <MembersList
        loading={loading}
        loadError={loadError}
        items={items}
        isOwner={isOwner}
        isPlatform={isPlatform}
        removeBusyId={removeBusyId}
        roleBusyId={roleBusyId}
        onRetry={refresh}
        onRemove={setRemoveTarget}
        onSetRole={handleSetRole}
      />

      {addOpen ? (
        <CreateMemberDialog onSuccess={handleAddSuccess} onCancel={() => setAddOpen(false)} />
      ) : null}

      <ConfirmModal
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && removeBusyId === null) setRemoveTarget(null);
        }}
        title={ptBR.members.removeConfirmTitle}
        description={
          removeTarget !== null
            ? `${ptBR.members.removeConfirmHint} (${removeTarget.email})`
            : ptBR.members.removeConfirmHint
        }
        confirmLabel={removeBusyId !== null ? ptBR.members.removing : ptBR.members.removeConfirmYes}
        cancelLabel={ptBR.members.removeConfirmNo}
        variant="destructive"
        onConfirm={handleRemoveConfirm}
        busy={removeBusyId !== null}
      />
    </section>
  );
}
